import { createSlice } from '@reduxjs/toolkit';

/**
 * Partner administration (Zezamii staff) — the create-partner + add/remove-member surface behind the
 * org-LESS, staff-gated /v5/partner-admin boundary. Distinct from `Partner` (per-org installer grants)
 * and the standalone Partner app (a partner's own cross-org view): this is the platform operator's view of every
 * partner company. A 403 (not staff) degrades quietly to `forbidden` — the page already gates on the
 * developer flag, so this is only a defensive backstop, never a red error.
 */
const initialState = {
	partners: [],
	listLoading: false,
	listLoaded: false,
	forbidden: false,
	error: null,

	// Open partner detail (partner + members + pendingInvites). Cleared when the drawer closes. Its own
	// error is kept separate from the list `error` so a drawer failure never paints the list page banner.
	detail: null,
	detailLoading: false,
	detailError: null,

	// Count of create / add-member / remove-member writes currently in flight. A counter (not a bool)
	// because writes run concurrently (takeEvery) — one finishing must not clear the flag for the others.
	mutatingCount: 0
};

const partnerAdminSlice = createSlice({
	name: 'partnerAdmin',
	initialState,
	reducers: {
		loadPartners: (state) => {
			state.listLoading = true;
			state.forbidden = false;
			state.error = null;
		},
		loadPartnersSuccess: (state, action) => {
			state.listLoading = false;
			state.listLoaded = true;
			state.partners = action.payload || [];
		},
		loadPartnersForbidden: (state) => {
			state.listLoading = false;
			state.listLoaded = true;
			state.forbidden = true;
			state.partners = [];
		},
		loadPartnersError: (state, action) => {
			state.listLoading = false;
			state.listLoaded = true; // resolved (unsuccessfully) — lets the page show the banner, not a bare empty table
			state.error = action.payload;
		},

		loadPartnerDetail: (state, action) => {
			state.detailLoading = true;
			state.detailError = null;
			// Switching to a different partner: drop the previous partner's detail so the drawer shows a
			// skeleton, never a flash of the wrong partner (a mutation reload can also land after close).
			if (state.detail && state.detail.partner?.partnerID !== action.payload?.partnerID) state.detail = null;
		},
		loadPartnerDetailSuccess: (state, action) => {
			state.detailLoading = false;
			state.detailError = null;
			state.detail = action.payload || null;
		},
		loadPartnerDetailError: (state, action) => {
			state.detailLoading = false;
			state.detailError = action.payload;
		},
		clearPartnerDetail: (state) => {
			state.detail = null;
			state.detailLoading = false;
			state.detailError = null;
		},

		// Writes — the saga runs each, notifies, then reloads the list (+ open detail). Each write bumps
		// the in-flight counter; `mutateComplete` (dispatched once per write, in the saga's finally)
		// decrements it, so overlapping writes only clear the flag once the last one settles.
		createPartner: (state) => {
			state.mutatingCount += 1;
		},
		addMember: (state) => {
			state.mutatingCount += 1;
		},
		removeMember: (state) => {
			state.mutatingCount += 1;
		},
		inviteMaster: (state) => {
			state.mutatingCount += 1;
		},
		resendInvite: (state) => {
			state.mutatingCount += 1;
		},
		revokeInvite: (state) => {
			state.mutatingCount += 1;
		},
		setMemberAdmin: (state) => {
			state.mutatingCount += 1;
		},
		mutateComplete: (state) => {
			state.mutatingCount = Math.max(0, state.mutatingCount - 1);
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
	}
});

export const partnerAdminActions = partnerAdminSlice.actions;
export default partnerAdminSlice.reducer;
